/**
 * JPEG dataURL 의 JFIF APP0 밀도(dpi) 태그를 기록한다 (BOOTH-35).
 * canvas.toDataURL 은 밀도를 쓰지 않아(또는 72dpi) 프린터 드라이버가 물리 크기를 잘못 잡을 수 있다.
 * APP0 가 있으면 units/Xdensity/Ydensity 를 덮어쓰고, 없으면 SOI 바로 뒤에 16바이트 APP0 를 삽입한다.
 * 실패 시 입력을 그대로 반환.
 */
const JFIF_ID = [0x4a, 0x46, 0x49, 0x46, 0x00]; // "JFIF\0"

export function withJpegDpi(dataUrl: string, dpi: number): string {
  if (!Number.isInteger(dpi) || dpi <= 0 || dpi > 65535) return dataUrl;
  const m = dataUrl.match(/^data:image\/jpeg;base64,(.+)$/);
  if (!m) return dataUrl;
  try {
    const bytes = base64ToBytes(m[1]);
    const patched = setJfifDensity(bytes, dpi);
    if (!patched) return dataUrl;
    return `data:image/jpeg;base64,${bytesToBase64(patched)}`;
  } catch {
    return dataUrl;
  }
}

/** 바이트 단위 패치. SOI 없으면 null. */
export function setJfifDensity(bytes: Uint8Array, dpi: number): Uint8Array | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

  // 기존 APP0(JFIF) 가 SOI 직후에 있으면 제자리 덮어쓰기
  if (
    bytes[2] === 0xff &&
    bytes[3] === 0xe0 &&
    bytes.length >= 18 &&
    JFIF_ID.every((b, i) => bytes[9 + i] === b)
  ) {
    const out = bytes.slice();
    out[13] = 1; // units: dots per inch
    out[14] = (dpi >> 8) & 0xff;
    out[15] = dpi & 0xff;
    out[16] = (dpi >> 8) & 0xff;
    out[17] = dpi & 0xff;
    return out;
  }

  // 없으면 삽입: FF E0 | len=16 | "JFIF\0" | ver 1.01 | units=1 | Xd | Yd | thumb 0×0
  const app0 = new Uint8Array([
    0xff, 0xe0, 0x00, 0x10,
    ...JFIF_ID,
    0x01, 0x01,
    0x01,
    (dpi >> 8) & 0xff, dpi & 0xff,
    (dpi >> 8) & 0xff, dpi & 0xff,
    0x00, 0x00,
  ]);
  const out = new Uint8Array(bytes.length + app0.length);
  out.set(bytes.subarray(0, 2), 0);
  out.set(app0, 2);
  out.set(bytes.subarray(2), 2 + app0.length);
  return out;
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}
